import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { FailureNotificationService } from 'app/modules/corrective-maintenance/services/failure-notification.service';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FailureNotification, FNStatus, OptionItem } from 'app/modules/corrective-maintenance/model';
import { debounceTime, Subscription, take } from 'rxjs';
import module from "app/modules/corrective-maintenance/corrective-maintenance.module";
import { RbacService } from 'app/core/auth/rbac/rbac.service';

@Component({
    selector: 'edit-fn-modal',
    templateUrl: './fn-modal.component.html',
    imports: module,
    standalone: true,
    styleUrls: ['./fn-modal.component.scss'],
})
export class FnModalComponent implements OnInit, OnDestroy {
    status = FNStatus;
    isUpdate: boolean = false;
    form: FormGroup;

    wellStringName: string;
    private optionSelectedFlag: boolean = false;
    private subscriptions: Subscription = new Subscription();

    options = {
        fields: [],
        platforms: [],
        wells: [],
        wellStrings: [],
        valveOptions: [],
        filteredValveOptions: [],
    };

    constructor(
        private formBuilder: FormBuilder,
        private service: FailureNotificationService,
        public dialogRef: MatDialogRef<FnModalComponent>,
        @Inject(MAT_DIALOG_DATA)
        public params: { payload: FailureNotification; action: string },
        private rbac: RbacService
    ) { }

    get title() {
        return (
            this.params.action.charAt(0).toUpperCase() +
            this.params.action.slice(1).toLowerCase()
        );
    }

    get isViewOnly() {
        return this.params.action == 'view' ? true : false;
    }

    get isDisabledSave() {
        return !([FNStatus.APPROVED, FNStatus.REJECTED].includes(
            this.params.payload.status as FNStatus
        )) && this.params.action != 'view';
    }

    get isApproverRoles(){
        return this.rbac.hasAny(['Well Integrity Senior Engineer','Lead Well Engineer'])
    }

    get isDraftRoles(){
        return this.rbac.hasAny(['Well Integrity Engineer','Well Integrity Desk Engineer'])
    }

    ngOnInit() {
        this.form = this.onForm();

        const stringSubscription = this.form
            .get('stringShortName')
            .valueChanges.subscribe((value) => this.autocomplete(value));
        this.subscriptions.add(stringSubscription);

        this.getValveOption()
    }
    
    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    getValveOption() {
        this.service
            .getValveOptionType()
            .pipe(debounceTime(300), take(1))
            .subscribe((res) => {
                this.options.valveOptions = res.data;
                this.options.filteredValveOptions = res.data;
            });
    }

    autocomplete(value: string) {

        if (!value) {
            this.form.get('wellStringId')?.setValue('');
            // this.form.markAllAsTouched()
            return
        };

        if (value.length < 3) return;

        this.service
            .getWellString({ wellstring: value })
            .pipe(debounceTime(300), take(1))
            .subscribe((res: any) => {
                this.options.wellStrings = res.data.wellStrings;

                // If not found
                if (this.options.wellStrings.length <= 0) {
                    this.options.wellStrings = [{
                        id: '',
                        stringShortName: 'Not Found',
                        stringLongName: 'Not Found',
                    }]
                }

                this.onSelectedWellString();
            });
    }

    onForm() {
        return this.formBuilder.group({
            id: new FormControl({
                value: this.params.payload?.id,
                disabled: true,
            }),
            wellStringId: new FormControl({
                value: this.params.payload?.wellStringId,
                disabled: this.isViewOnly,
            }),
            stringShortName: new FormControl({
                value: this.params.payload?.stringShortName,
                disabled: this.isViewOnly,
            },[Validators.required]),
            status: new FormControl({
                value: this.params.payload?.status,
                disabled: true,
            }),
            preparedBy: new FormControl({
                value: this.params.payload?.preparedBy,
                disabled: true,
            }),
            approvedBy: new FormControl({
                value: this.params.payload?.approvedBy,
                disabled: true,
            }),
            failEquipment: new FormControl({
                value: this.params.payload?.failEquipment,
                disabled: this.isViewOnly,
            },[Validators.required]),
            failureDate: new FormControl({
                value: this.params.payload?.failureDate,
                disabled: this.isViewOnly,
            },[Validators.required]),
            problemReported: new FormControl({
                value: this.params.payload?.problemReported,
                disabled: this.isViewOnly,
            },[Validators.required]),
            detailDesc: new FormControl({
                value: this.params.payload?.detailDesc,
                disabled: this.isViewOnly,
            },[Validators.required]),
            forwardActionPlan: new FormControl({
                value: this.params.payload?.forwardActionPlan,
                disabled: this.isViewOnly,
            },[Validators.required]),
            isWellHealthy: new FormControl({
                value: this.params.payload?.isWellHealthy,
                disabled: this.isViewOnly,
            },[Validators.required]),
            riskRanking: new FormControl({
                value: this.params.payload?.riskRanking,
                disabled: this.isViewOnly,
            },[Validators.required]),
        });
    }

    save(status = null) {
        const payload = this.form.getRawValue();

        try {
            payload.failureDate = payload.failureDate?.toSQLDate();
        } catch (e) { }

        if (status) payload.status = status;

        if (!payload.status) payload.status = FNStatus.DRAFT;

        this.service.save(payload).subscribe((response) => {
            this.isUpdate = true;
            this.dialogRef.close({ isUpdate: this.isUpdate });
        });
    }

    onSelectedWellString() {
        const value = this.form.get('stringShortName').value;
        const result = this.options.wellStrings.filter((row: any) => {
            return (
                row.stringShortName.toLowerCase() ===
                value.toLowerCase()
            );
        });

        if (result.length != 1) {
            this.form.get('wellStringId')?.setValue('');
            return;
        }

        this.form.get('wellStringId')?.setValue(result[0].id);
        this.optionSelectedFlag = true;
        this.options.wellStrings = []
    }

    validateWellStringSelection() {
        // If user didn't pick from the list, clear input
        if (!this.optionSelectedFlag && !this.form.get('wellStringId')?.value) {
            this.form.get('stringShortName')?.setValue('');
        }

        this.optionSelectedFlag = false; // reset for next time
    }

    disabledSubmit(): boolean {
        if (this.form.invalid) return true;
        if (this.params.action == 'create') return false
        
        const status = this.params?.payload.status;

        // What actions are allowed right now?
        const canDraftSubmit = this.isDraftRoles && status === this.status.DRAFT && this.form.valid;
        const canApprove     = this.isApproverRoles && status === this.status.READY; // add && this.form.valid if you require it
      
        // Disable when none of the allowed actions apply
        return !(canDraftSubmit || canApprove);
      }
}
